import { v4 as uuidv4 } from "uuid";
import { useActiveCanvas } from "@/utils/utils";
import { SCREEN_SIZE_NAME_TO_SIZE, STORYBOARD_TYPES } from "@/utils/constants";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCallback, useEffect, useRef } from "react";
import { Storyboard } from "@/utils/types";

/**
 * This creates the storyboard object in the DB so it shows up on the canvas,
 * but does _not_ create it in the codebase. This can be used outside of createStoryboard
 * in cases where it only needs to be initialized locally (e.g. when drawing a storyboard)
 */
export const useInitializeStoryboard = () => {
  const canvas = useActiveCanvas();
  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  return useCallback(
    (
      pointOnCanvasX: number,
      pointOnCanvasY: number,
      width: number,
      height: number,
      idToUse?: string,
      name?: string,
      designSuggestionId?: string,
      isPlaceholder?: boolean,
      expectedFullFilePath?: string,
      applicationType?: boolean
    ): Storyboard => {
      const maxZIndex = canvasRef.current?.storyboards
        ?.filter(
          (storyboard: any) =>
            storyboard.deleted !== true && !storyboard.preCreated
        )
        ?.map((storyboard: any) => storyboard.zIndex)
        ?.reduce((a: any, b: any) => Math.max(a, b), 0);

      if (applicationType) {
        const initialSize = SCREEN_SIZE_NAME_TO_SIZE["MacBook Pro 14”"];

        return {
          id: idToUse || uuidv4().toString(),
          type: STORYBOARD_TYPES.APPLICATION,
          width: width || initialSize.width,
          height: height || initialSize.height,
          x: pointOnCanvasX,
          y: pointOnCanvasY,
          zIndex: maxZIndex + 10,
        };
      }

      // Ensure there is no name collision with other storyboards or components in the codebase
      let nameToUse = name;
      if (!nameToUse && !applicationType) {
        const storyboardAndComponentNames = new Set();

        const treeElementLookup = useCodebaseStore.getState().treeElementLookup;

        // @ts-expect-error TS18047
        canvasRef.current.storyboards
          .filter(
            (storyboard: any) =>
              storyboard.type !== STORYBOARD_TYPES.APPLICATION
          )
          .filter(
            (storyboard: any) =>
              storyboard.deleted !== true && !storyboard.preCreated
          )
          .forEach((storyboard: any) => {
            storyboardAndComponentNames.add(storyboard.name);
          });

        Object.values(treeElementLookup).forEach((treeElement: any) => {
          if (treeElement.type === "component") {
            storyboardAndComponentNames.add(treeElement.name);
          }
        });

        let storyboardIndex = 1;
        while (
          storyboardAndComponentNames.has(`Storyboard${storyboardIndex}`)
        ) {
          storyboardIndex++;
        }

        nameToUse = `Storyboard${storyboardIndex}`;
      }

      let idToSet = idToUse;
      if (!idToSet && !applicationType) {
        // Try to get a storyboard that was pre-created
        const preCreatedStoryboard = canvasRef.current?.storyboards?.find(
          (sb) => sb.preCreated
        );
        if (preCreatedStoryboard) {
          idToSet = preCreatedStoryboard.id;
        }
      }

      const newStoryboard: Storyboard = {
        id: idToSet || uuidv4().toString(),
        type: STORYBOARD_TYPES.COMPONENT,
        width,
        height,
        x: pointOnCanvasX,
        y: pointOnCanvasY,
        zIndex: maxZIndex + 10,
        name: nameToUse,
        created: false,
        designSuggestionId,
        isPlaceholder,
        fullFilePath: expectedFullFilePath,
      };

      return newStoryboard;
    },
    []
  );
};

export const useInitializeStoryStoryboard = () => {
  const canvas = useActiveCanvas();
  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  return useCallback(
    (
      pointOnCanvasX: number,
      pointOnCanvasY: number,
      width: number,
      height: number,
      name: string,
      idToUse?: string
    ): Storyboard => {
      // Ensure there is no name collision with other storyboards or components in the codebase
      const storyboardAndComponentNames = new Set();

      const treeElementLookup = useCodebaseStore.getState().treeElementLookup;

      // @ts-expect-error TS18047
      canvasRef.current.storyboards
        .filter(
          (storyboard: any) => storyboard.type !== STORYBOARD_TYPES.APPLICATION
        )
        .filter(
          (storyboard: any) =>
            storyboard.deleted !== true && !storyboard.preCreated
        )
        .forEach((storyboard: any) => {
          storyboardAndComponentNames.add(storyboard.name);
        });

      Object.values(treeElementLookup).forEach((treeElement: any) => {
        if (treeElement.type === "component") {
          storyboardAndComponentNames.add(treeElement.name);
        }
      });

      let storyboardIndex = 1;
      while (
        storyboardAndComponentNames.has(
          `${name}${storyboardIndex === 1 ? "" : storyboardIndex}`
        )
      ) {
        storyboardIndex++;
      }

      const maxZIndex = canvasRef.current?.storyboards
        ?.filter(
          (storyboard: any) =>
            storyboard.deleted !== true && !storyboard.preCreated
        )
        ?.map((storyboard: any) => storyboard.zIndex)
        ?.reduce((a: any, b: any) => Math.max(a, b), 0);

      return {
        id: idToUse || uuidv4().toString(),
        type: STORYBOARD_TYPES.STORY,
        width,
        height,
        x: pointOnCanvasX,
        y: pointOnCanvasY,
        zIndex: maxZIndex + 10,
        name: `${name}${storyboardIndex === 1 ? "" : storyboardIndex}`,
        created: false,
      };
    },
    []
  );
};
